<script lang="ts">
  import type { SidePanel } from '../panel-sections.svelte';

  /**
   * `edge` is the side of the panel the handle sits on: a panel left of the canvas resizes
   * by its right edge, one right of it by its left edge.
   */
  const {
    panel,
    edge,
    label,
    controls,
    locked,
  }: {
    panel: SidePanel;
    edge: 'left' | 'right';
    label: string;
    controls: string;
    locked: boolean;
  } = $props();

  const KEY_STEP = 16;
  // Dragging toward the canvas widens the panel.
  const sign = $derived(edge === 'right' ? 1 : -1);
  let drag: { pointerX: number; width: number } | null = null;

  function startResize(event: PointerEvent & { currentTarget: HTMLElement }) {
    if (locked || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag = { pointerX: event.clientX, width: panel.width };
  }

  function followResize(event: PointerEvent) {
    if (drag) panel.resize(drag.width + sign * (event.clientX - drag.pointerX));
  }

  function endResize() {
    if (!drag) return;
    drag = null;
    panel.finishResize();
  }

  function resetWidth() {
    if (locked) return;
    panel.resize(panel.limits.default);
    panel.finishResize();
  }

  function resizeByKey(event: KeyboardEvent) {
    const step = { ArrowLeft: -KEY_STEP, ArrowRight: KEY_STEP }[event.key];
    if (step === undefined || locked) return;
    event.preventDefault();
    panel.resize(panel.width + sign * step);
    panel.finishResize();
  }
</script>

<!-- A focusable separator is the ARIA window-splitter pattern, which Svelte's a11y rules
     count as non-interactive. -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
<div
  class="resize {edge}"
  class:locked
  role="separator"
  aria-label={label}
  aria-orientation="vertical"
  aria-controls={controls}
  aria-valuemin={panel.limits.min}
  aria-valuemax={panel.limits.max}
  aria-valuenow={panel.width}
  tabindex="0"
  title="Drag to resize, double-click to reset"
  onpointerdown={startResize}
  onpointermove={followResize}
  onpointerup={endResize}
  onpointercancel={endResize}
  ondblclick={resetWidth}
  onkeydown={resizeByKey}
></div>

<style>
  .resize {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    width: 7px;
    cursor: col-resize;
    touch-action: none;
  }

  .resize.right {
    right: -4px;
  }

  .resize.left {
    left: -4px;
  }

  .resize:hover,
  .resize:focus-visible {
    outline: none;
    background: color-mix(in srgb, var(--color-accent) 35%, transparent);
  }

  .resize.locked {
    cursor: default;
    background: none;
  }
</style>
